/**
 * Copied from BBC's color-contrast-checker https://github.com/bbc/color-contrast-checker/
 * and manually defined to reduce a dependency on a package that hasn't been updated in 2 years.
 * Slightly updated to use TypeScript and avoid linting errors.
 *
 * For WCAG's purposes, large text is defined as 24px or larger, or 18.5px or larger when bold.
 * This checker does not account for bold large text, it just defines large text as 18.5px or larger.
 */
'use strict';
/**
 * Color Contast Checker
 * An accessibility checker tool for validating the color contrast based on WCAG 2.0 standard.
 * var ccc = new ColorContrastChecker();

 * var color1 = "#FFFFFF";
 * var color2 = "#000000;

 * if (ccc.isLevelAA(color1, color2, 14)) {
 *     alert("Valid Level AA");
 * } else {
 *     alert("Invalid Contrast");
 * }
 */
export class ColorContrastChecker {
    fontSize = 14;
    rgbClass = {
        r: 0,
        g: 0,
        b: 0,
        toString: function () {
            return '<r: ' + this.r + ' g: ' + this.g + ' b: ' + this.b + ' >';
        },
    };
    isValidSixDigitColorCode(hex) {
        const regSixDigitColorcode = /^(#)?([0-9a-fA-F]{6})?$/;
        return regSixDigitColorcode.test(hex);
    }
    isValidThreeDigitColorCode(hex) {
        const regThreeDigitColorcode = /^(#)?([0-9a-fA-F]{3})?$/;
        return regThreeDigitColorcode.test(hex);
    }
    isValidColorCode(hex) {
        return this.isValidSixDigitColorCode(hex) || this.isValidThreeDigitColorCode(hex);
    }
    isValidRatio(ratio) {
        return typeof ratio === 'number';
    }
    convertColorToSixDigit(hex) {
        return '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
    }
    hexToLuminance(color) {
        if (!this.isValidColorCode(color)) {
            throw new Error('Invalid Color :' + color);
        }
        if (this.isValidThreeDigitColorCode(color)) {
            color = this.convertColorToSixDigit(color);
        }
        const LRGB = this.calculateLRGB(this.getRGBFromHex(color));
        return this.calculateLuminance(LRGB);
    }
    getContrastRatio(lumA, lumB) {
        let lighter, darker;
        if (lumA >= lumB) {
            lighter = lumA;
            darker = lumB;
        }
        else {
            lighter = lumB;
            darker = lumA;
        }
        const ratio = (lighter + 0.05) / (darker + 0.05);
        return ratio;
    }
    calculateLuminance(lRGB) {
        return 0.2126 * lRGB.r + 0.7152 * lRGB.g + 0.0722 * lRGB.b;
    }
    calculateLRGB(rgb) {
        const sRGB = this.calculateSRGB(rgb);
        const lRGB = Object.create(this.rgbClass);
        let val = 0;
        for (const key in sRGB) {
            if (Object.prototype.hasOwnProperty.call(sRGB, key)) {
                val = parseFloat(sRGB[key]);
                if (val <= 0.03928) {
                    lRGB[key] = val / 12.92;
                }
                else {
                    lRGB[key] = Math.pow((val + 0.055) / 1.055, 2.4);
                }
            }
        }
        return lRGB;
    }
    calculateSRGB(rgb) {
        const sRGB = Object.create(this.rgbClass);
        for (const key in rgb) {
            if (Object.prototype.hasOwnProperty.call(rgb, key)) {
                sRGB[key] = parseFloat((rgb[key] / 255).toString());
            }
        }
        return sRGB;
    }
    getRGBFromHex(color) {
        if (typeof color !== 'string') {
            throw new Error('must use string');
        }
        const rgb = Object.create(this.rgbClass);
        const rVal = parseInt(color.slice(1, 3), 16);
        const gVal = parseInt(color.slice(3, 5), 16);
        const bVal = parseInt(color.slice(5, 7), 16);
        rgb.r = rVal;
        rgb.g = gVal;
        rgb.b = bVal;
        return rgb;
    }
    verifyContrastRatio(ratio) {
        const resultsClass = {
            toString: function () {
                return ('< WCAG-AA: ' + (this.WCAG_AA ? 'pass' : 'fail') + ' WCAG-AAA: ' + (this.WCAG_AAA ? 'pass' : 'fail') + ' >');
            },
        };
        const WCAG_REQ_RATIO_AA_LG = 3.0, WCAG_REQ_RATIO_AA_SM = 4.5, WCAG_REQ_RATIO_AAA_LG = 4.5, WCAG_REQ_RATIO_AAA_SM = 7.0, WCAG_FONT_CUTOFF = 18.5;
        const results = Object.create(resultsClass), fontSize = this.fontSize || 14;
        if (fontSize >= WCAG_FONT_CUTOFF) {
            results.WCAG_AA = ratio >= WCAG_REQ_RATIO_AA_LG;
            results.WCAG_AAA = ratio >= WCAG_REQ_RATIO_AAA_LG;
        }
        else {
            results.WCAG_AA = ratio >= WCAG_REQ_RATIO_AA_SM;
            results.WCAG_AAA = ratio >= WCAG_REQ_RATIO_AAA_SM;
        }
        return results;
    }
    verifyCustomContrastRatio(inputRatio, checkRatio) {
        const resultsClass = {
            toString: function () {
                return '< Custom Ratio: ' + (this.customRatio ? 'pass' : 'fail') + '  >';
            },
        };
        const results = Object.create(resultsClass);
        results.customRatio = inputRatio >= checkRatio;
        return results;
    }
    check(colorA, colorB, fontSize, customRatio) {
        if (typeof fontSize !== 'undefined') {
            this.fontSize = fontSize;
        }
        if (!colorA || !colorB) {
            return { WCAG_AA: false, WCAG_AAA: false };
        }
        const l1 = this.hexToLuminance(colorA); /* higher value */
        const l2 = this.hexToLuminance(colorB); /* lower value */
        const contrastRatio = this.getContrastRatio(l1, l2);
        if (typeof customRatio !== 'undefined') {
            if (!this.isValidRatio(customRatio)) {
                return { customRatio: false };
            }
            return this.verifyCustomContrastRatio(contrastRatio, customRatio);
        }
        else {
            return this.verifyContrastRatio(contrastRatio);
        }
    }
    isLevelAA(colorA, colorB, fontSize) {
        const result = this.check(colorA, colorB, fontSize);
        if ('WCAG_AA' in result) {
            return result.WCAG_AA;
        }
        return false;
    }
    isLevelAAA(colorA, colorB, fontSize) {
        const result = this.check(colorA, colorB, fontSize);
        if ('WCAG_AAA' in result) {
            return result.WCAG_AAA;
        }
        return false;
    }
    isLevelCustom(colorA, colorB, ratio) {
        const result = this.check(colorA, colorB, void 0, ratio);
        if ('customRatio' in result) {
            return result.customRatio;
        }
        return false;
    }
}
